import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import AntIcon from "@/components/icons/AntIcon";
import FileTypeIcon from "@/components/icons/FileTypeIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { InputNumber } from "@/components/InputNumber";
import Ellipsis from "@/components/views/Ellipsis";
import { selectStandardProps, tableStandardProps } from "@/utils/componentUtils";
import { Decimal } from "@/utils/decimal";
import {
  formatLocaleCurrency,
  formatLocaleDate,
  formatLocaleNettoPoints,
  formatLocalePercentageNumber,
} from "@/utils/formatUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { useWindowSize } from "@/utils/hooksUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Form,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Upload,
} from "antd";
import { RcFile } from "antd/lib/upload";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import {
  useDeleteCommissionsSettingsAttachment,
  useGetAgentCommissionsSettingsRankIntervals,
  useTogglePointsUnificationCommissionsSettings,
  useTogglePrimaryManagerialCommissions,
  useTogglePrimaryPaymentInclusionCommissionsSettings,
  useTogglePromotionAllowanceCommissionsSettings,
  useToggleSecondaryManagerialCommissions,
  useToggleSecondaryPaymentInclusionCommissionsSettings,
  useToggleUsageAsHeadCommissionsSettings,
  useUnassignCommissionsSettingsLevel,
  useUpdateInitialPointsCommissionsSettings,
  useUpdateStatusCommissionsSettings,
  useUploadCommissionsSettingsAttachments,
} from "../../../common/api/queries";
import { ModalSizes, ViewBreakpoints } from "../../../common/constants";
import { TopAgentType } from "../../../common/enums";
import { Permission } from "../../../common/security/enums";
import { UUID } from "../../../typings/global";
import { useHasPermissions } from "../../auth/hooks";
import { COMMISSIONS_LEVEL_ROUTE_PATHS } from "../../commissions/level/paths";
import { CommissionsSettingsLevelAgentProfile } from "../../commissions/level/types";
import { formatCommissionsLevelName } from "../../commissions/level/utils";
import { CommissionsSettingsRankBase } from "../../commissions/ranks/types";
import { CommissionsSettingsAssignLevelForm } from "../../commissions/settings/components/forms/CommissionsSettingsAssignLevelForm";
import { CommissionsSettingsAssignRankModalForm } from "../../commissions/settings/components/forms/CommissionsSettingsAssignRankModalForm";
import { CommissionsSettingsStatus } from "../../commissions/settings/enums";
import {
  CommissionsSettings,
  CommissionsSettingsAgentProfile,
  CommissionsSettingsRankInterval,
  UpdateCommissionsSettingsInitialPoints,
} from "../../commissions/settings/types";
import { isCommissionsSettings } from "../../commissions/settings/utils";
import { AGENT_ROUTE_PATHS } from "../paths";
import { AgentBase } from "../types";

type LevelProps = {
  isTopAgentTypePlus: boolean;
  commissionsSettings: CommissionsSettings | CommissionsSettingsAgentProfile;
  level?: CommissionsSettingsLevelAgentProfile;
  hasActionsPermission: boolean;
};

const Level = ({ level, isTopAgentTypePlus, commissionsSettings, hasActionsPermission }: LevelProps) => {
  const { t } = useTranslation();

  const [assignLevelFormOpen, setAssignLevelFormOpen] = useState(false);

  const hasTopAgentCommissionsPermission = useHasPermissions(Permission.TOP_AGENT_COMMISSIONS);

  const unassignCommissionsSettingsLevel = useUnassignCommissionsSettingsLevel();

  const renderTitle = () => {
    if (!level) {
      return <span className="sub-header-info dashed">{t("common.none")}</span>;
    }

    const levelName = formatCommissionsLevelName(level);

    return hasTopAgentCommissionsPermission ? (
      <Link to={generatePath(COMMISSIONS_LEVEL_ROUTE_PATHS.detail.to, { id: level.id })} target="_blank">
        {levelName}
      </Link>
    ) : (
      levelName
    );
  };

  const renderActions = (commissionsSettings: CommissionsSettings) => {
    if (!level) {
      return (
        <Button
          size="small"
          icon={<AntIcon type="sister-node" />}
          color="primary"
          variant="solid"
          onClick={() => setAssignLevelFormOpen(true)}
        >
          {t("common.assign")}
        </Button>
      );
    }

    return (
      <>
        <Button
          icon={<AntIcon type="edit" />}
          color="primary"
          variant="solid"
          size="small"
          onClick={() => setAssignLevelFormOpen(true)}
        >
          {t("common.edit")}
        </Button>
        <Popconfirm
          title={t("commissions.settings.helpers.unassignLevelConfirm")}
          icon={<AntIcon type="question" />}
          okText={t("common.yes")}
          cancelText={t("common.no")}
          okType="primary"
          onConfirm={() =>
            unassignCommissionsSettingsLevel.mutate({
              id: commissionsSettings.agent.id,
              object: {
                optimisticLockVersion: commissionsSettings.optimisticLockVersion,
              },
            })
          }
        >
          <Button size="small" icon={<AntIcon type="delete" />} danger>
            {t("common.remove")}
          </Button>
        </Popconfirm>
      </>
    );
  };

  return (
    <Flex justify="space-between">
      {renderTitle()}

      {isCommissionsSettings(commissionsSettings) && hasActionsPermission && (
        <>
          <Space>{renderActions(commissionsSettings)}</Space>

          <CommissionsSettingsAssignLevelForm
            isTopAgentTypePlus={isTopAgentTypePlus}
            open={assignLevelFormOpen}
            settings={commissionsSettings}
            onFormCancel={() => setAssignLevelFormOpen(false)}
          />
        </>
      )}
    </Flex>
  );
};

type RankProps = {
  hasActionsPermission: boolean;
  rank?: CommissionsSettingsRankBase;
  agentId: UUID;
};

const Rank = ({ hasActionsPermission, agentId, rank }: RankProps) => {
  const { t } = useTranslation();

  const [assignRankFormOpen, setAssignRankFormOpen] = useState(false);
  const { data: rankIntervals, isFetching } = useGetAgentCommissionsSettingsRankIntervals(agentId);

  const renderActions = () => {
    return (
      <Button
        icon={rank ? <AntIcon type="edit" /> : <AntIcon type="sister-node" />}
        size="small"
        color="primary"
        variant="solid"
        onClick={() => setAssignRankFormOpen(true)}
      >
        {rank ? t("common.edit") : t("common.assign")}
      </Button>
    );
  };

  return (
    <Flex justify="space-between">
      {rank ? (
        <LabelWithPopover
          popoverContent={
            <Table<CommissionsSettingsRankInterval>
              {...tableStandardProps()}
              style={{ height: numberOrZero(rankIntervals?.length) > 5 ? 280 : undefined }}
              dataSource={rankIntervals}
              loading={isFetching}
              columns={[
                {
                  key: "name",
                  width: 250,
                  title: t("commissions.rank.attrs.intervals.rankId"),
                  ellipsis: { showTitle: false },
                  render: (_, record) => <Ellipsis>{record.rank.name}</Ellipsis>,
                },
                {
                  key: "startDate",
                  title: t("commissions.rank.attrs.intervals.startDate"),
                  width: 100,
                  render: (_, record) => formatLocaleDate(record.startDate),
                },
                {
                  key: "endDate",
                  title: t("commissions.rank.attrs.intervals.endDate"),
                  width: 100,
                  render: (_, record) => (record.endDate ? formatLocaleDate(record.endDate) : undefined),
                },
              ]}
              pagination={numberOrZero(rankIntervals?.length) > 5 ? { placement: ["bottomEnd"], pageSize: 5 } : false}
            />
          }
          label={rank.name}
        />
      ) : (
        <span className="sub-header-info dashed">{t("common.none")}</span>
      )}

      {hasActionsPermission && (
        <>
          <Space>{renderActions()}</Space>

          <CommissionsSettingsAssignRankModalForm
            isOpen={assignRankFormOpen}
            agentId={agentId}
            onFormCancel={() => setAssignRankFormOpen(false)}
          />
        </>
      )}
    </Flex>
  );
};

type IndividualPointsProps = {
  data: {
    initialPrimaryIndividualPoints: number;
    initialPrimaryGroupPoints: number;
    initialSecondaryIndividualPoints: number;
    initialSecondaryGroupPoints: number;
    initialBonusIndividualPoints: number;
    initialBonusGroupPoints: number;
    optimisticLockVersion?: number;
  };
  agentId: UUID;
  hasActionsPermission: boolean;
};

const IndividualPoints = ({ data, agentId, hasActionsPermission }: IndividualPointsProps) => {
  const { t } = useTranslation();

  const windowSize = useWindowSize();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const updateInitialPointsCommissionsSettings = useUpdateInitialPointsCommissionsSettings();

  const [form] = Form.useForm<UpdateCommissionsSettingsInitialPoints>();

  useEffect(() => {
    if (isModalOpen) {
      form.setFieldsValue(data);
    }
  }, [isModalOpen]);

  return (
    <Flex justify="space-between">
      <Descriptions
        column={windowSize.innerWidth < ViewBreakpoints.DESKTOP ? 1 : 3}
        bordered
        size="small"
        layout="vertical"
        styles={{ content: { textAlign: "right", whiteSpace: "nowrap" }, label: { wordBreak: "normal" } }}
        items={[
          {
            key: "initialPrimaryIndividualPoints",
            label: t("commissions.settings.helpers.initialPrimaryIndividualPoints"),
            children: formatLocaleNettoPoints(data.initialPrimaryIndividualPoints),
          },
          {
            key: "initialSecondaryIndividualPoints",
            label: t("commissions.settings.helpers.initialSecondaryIndividualPoints"),
            children: formatLocaleNettoPoints(data.initialSecondaryIndividualPoints),
          },
          {
            key: "initialBonusIndividualPoints",
            label: t("commissions.settings.helpers.initialBonusIndividualPoints"),
            children: formatLocaleNettoPoints(data.initialBonusIndividualPoints),
          },
          {
            key: "initialPrimaryGroupPoints",
            label: t("commissions.settings.helpers.initialPrimaryGroupPoints"),
            children: formatLocaleNettoPoints(data.initialPrimaryGroupPoints),
          },
          {
            key: "initialSecondaryGroupPoints",
            label: t("commissions.settings.helpers.initialSecondaryGroupPoints"),
            children: formatLocaleNettoPoints(data.initialSecondaryGroupPoints),
          },
          {
            key: "initialBonusGroupPoints",
            label: t("commissions.settings.helpers.initialBonusGroupPoints"),
            children: formatLocaleNettoPoints(data.initialBonusGroupPoints),
          },
        ]}
      />

      {hasActionsPermission && (
        <Button
          className="margin-left-small"
          size="small"
          icon={<AntIcon type="edit" />}
          color="primary"
          variant="solid"
          onClick={() => setIsModalOpen(true)}
        >
          {t("common.edit")}
        </Button>
      )}

      <Modal
        open={isModalOpen}
        width={ModalSizes.TINY}
        title={t("commissions.settings.titles.updateInitialPoints")}
        mask={{ closable: false }}
        confirmLoading={updateInitialPointsCommissionsSettings.isPending}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        afterClose={() => form.resetFields()}
        onCancel={() => setIsModalOpen(false)}
        onOk={() =>
          form.validateFields().then((values) =>
            updateInitialPointsCommissionsSettings.mutate(
              { id: agentId, object: values },
              {
                onSuccess: () => setIsModalOpen(false),
                onError: (error) => setErrorsToForm(form, "commissions.settings.attrs", error.violations),
              },
            ),
          )
        }
      >
        <Form form={form} layout="vertical" name="updateInitialPoints">
          <HiddenInput name="optimisticLockVersion" />

          <Form.Item
            name="initialPrimaryIndividualPoints"
            label={t("commissions.settings.attrs.initialPrimaryIndividualPoints")}
            rules={[validations.notNull]}
          >
            <InputNumber type="decimal" addon="nettoPoints" />
          </Form.Item>

          <Form.Item
            name="initialPrimaryGroupPoints"
            label={t("commissions.settings.attrs.initialPrimaryGroupPoints")}
            rules={[validations.notNull]}
          >
            <InputNumber type="decimal" addon="nettoPoints" />
          </Form.Item>

          <Form.Item
            name="initialSecondaryIndividualPoints"
            label={t("commissions.settings.attrs.initialSecondaryIndividualPoints")}
            rules={[validations.notNull]}
          >
            <InputNumber type="decimal" addon="nettoPoints" />
          </Form.Item>

          <Form.Item
            name="initialSecondaryGroupPoints"
            label={t("commissions.settings.attrs.initialSecondaryGroupPoints")}
            rules={[validations.notNull]}
          >
            <InputNumber type="decimal" addon="nettoPoints" />
          </Form.Item>

          <Form.Item
            name="initialBonusIndividualPoints"
            label={t("commissions.settings.attrs.initialBonusIndividualPoints")}
            rules={[validations.notNull]}
          >
            <InputNumber type="decimal" addon="nettoPoints" />
          </Form.Item>

          <Form.Item
            name="initialBonusGroupPoints"
            label={t("commissions.settings.attrs.initialBonusGroupPoints")}
            rules={[validations.notNull]}
          >
            <InputNumber type="decimal" addon="nettoPoints" />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

interface Props {
  agent: AgentBase;
  commissionsSettings: CommissionsSettings | CommissionsSettingsAgentProfile;
  viewMode?: boolean;
}

export const AgentCommissionsSummary = ({ agent, commissionsSettings, viewMode }: Props) => {
  const { t } = useTranslation();

  const isTopAgentTypePlus = agent?.topAgentType === TopAgentType.PLUS;

  const windowSize = useWindowSize();

  const hasManageAgentCommissionsSettingsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS_SETTINGS_MANAGE);

  const hasActionsPermission = !viewMode && hasManageAgentCommissionsSettingsPermission;

  const uploadCommissionsSettingsAttachment = useUploadCommissionsSettingsAttachments();
  const deleteCommissionsSettingsAttachment = useDeleteCommissionsSettingsAttachment();
  const updateStatusCommissionsSettings = useUpdateStatusCommissionsSettings();
  const togglePromotionAllowanceCommissionsSettings = useTogglePromotionAllowanceCommissionsSettings();
  const togglePrimaryPaymentInclusionCommissionsSettings = useTogglePrimaryPaymentInclusionCommissionsSettings();
  const toggleSecondaryPaymentInclusionCommissionsSettings = useToggleSecondaryPaymentInclusionCommissionsSettings();
  const toggleUsageAsHeadCommissionsSettings = useToggleUsageAsHeadCommissionsSettings();
  const togglePointsUnificationCommissionsSettings = useTogglePointsUnificationCommissionsSettings();
  const togglePrimaryManagerialCommissions = useTogglePrimaryManagerialCommissions();
  const toggleSecondaryManagerialCommissions = useToggleSecondaryManagerialCommissions();

  const handleAttachmentsUpload = (_: RcFile, fileList: RcFile[]): boolean => {
    if (fileList.length) {
      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      fileList.splice(0, fileList.length);

      uploadCommissionsSettingsAttachment.mutate({ id: agent.id, object: formData });
    }

    return false;
  };

  return (
    <>
      <Flex justify="space-between" gap={24} className="margin-top-small">
        <div style={{ flex: 3 }}>
          <Descriptions
            column={1}
            bordered
            styles={{ label: { width: 200, fontWeight: "bold", fontSize: 16 } }}
            items={[
              {
                key: "level",
                label: t("commissions.settings.helpers.assignedLevel"),
                children: (
                  <Level
                    level={commissionsSettings.level}
                    commissionsSettings={commissionsSettings}
                    isTopAgentTypePlus={isTopAgentTypePlus}
                    hasActionsPermission={hasActionsPermission}
                  />
                ),
              },
              {
                key: "rank",
                label: t("commissions.settings.helpers.assignedRank"),
                children: (
                  <Rank
                    hasActionsPermission={hasActionsPermission}
                    rank={commissionsSettings.rank}
                    agentId={agent.id}
                  />
                ),
              },
              isTopAgentTypePlus
                ? {
                    key: "individualPoints",
                    label: t("commissions.settings.helpers.individualPoints"),
                    children: (
                      <IndividualPoints
                        data={{
                          initialPrimaryIndividualPoints: commissionsSettings.initialPrimaryIndividualPoints,
                          initialSecondaryIndividualPoints: commissionsSettings.initialSecondaryIndividualPoints,
                          initialPrimaryGroupPoints: commissionsSettings.initialPrimaryGroupPoints,
                          initialSecondaryGroupPoints: commissionsSettings.initialSecondaryGroupPoints,
                          initialBonusIndividualPoints: commissionsSettings.initialBonusIndividualPoints,
                          initialBonusGroupPoints: commissionsSettings.initialBonusGroupPoints,
                          optimisticLockVersion: isCommissionsSettings(commissionsSettings)
                            ? commissionsSettings.optimisticLockVersion
                            : undefined,
                        }}
                        agentId={agent.id}
                        hasActionsPermission={hasActionsPermission}
                      />
                    ),
                  }
                : undefined,
              {
                key: "points",
                label: t("commissions.settings.titles.points"),
                children: (
                  <Descriptions
                    column={1}
                    bordered
                    size="small"
                    style={{ maxWidth: 400 }}
                    styles={{ content: { textAlign: "right" } }}
                    items={[
                      {
                        key: "individualPointAmount",
                        label: t("commissions.settings.attrs.individualPointAmount"),
                        children: formatLocaleNettoPoints(commissionsSettings.individualPointAmount),
                      },
                      {
                        key: "groupPointAmount",
                        label: t("commissions.settings.attrs.groupPointAmount"),
                        children: formatLocaleNettoPoints(commissionsSettings.groupPointAmount),
                      },
                      {
                        key: "carrierPointsAmount",
                        label: t("commissions.settings.helpers.carrierPointsAmount"),
                        children: formatLocaleNettoPoints(
                          Decimal.sum(
                            Decimal.round(commissionsSettings.individualPointAmount),
                            Decimal.round(commissionsSettings.groupPointAmount),
                          ),
                        ),
                      },
                    ]}
                  />
                ),
              },
              commissionsSettings.bailAccountSettings
                ? {
                    key: "bailAccount",
                    label: t("commissions.bailAccount.titles.page"),
                    children: (
                      <Descriptions
                        column={1}
                        bordered
                        size="small"
                        style={{ maxWidth: 400 }}
                        styles={{ content: { textAlign: "right" } }}
                        items={[
                          ...(isTopAgentTypePlus
                            ? [
                                {
                                  key: "primaryTipperBailAccountBalance",
                                  label: t("commissions.bailAccount.helpers.primaryTipperBailAccountBalance_PLUS"),
                                  children: formatLocaleCurrency(
                                    commissionsSettings.primaryTipperBailAccountBalance ?? 0,
                                  ),
                                },
                                {
                                  key: "primaryBrokerBailAccountBalance",
                                  label: t("commissions.bailAccount.helpers.primaryBrokerBailAccountBalance_PLUS"),
                                  children: formatLocaleCurrency(
                                    commissionsSettings.primaryBrokerBailAccountBalance ?? 0,
                                  ),
                                },
                              ]
                            : [
                                {
                                  key: "bailAccountBalance",
                                  label: t("commissions.bailAccount.helpers.currentBalance"),
                                  children: formatLocaleCurrency(commissionsSettings.bailAccountBalance ?? 0),
                                },
                              ]),
                          {
                            key: "targetAmount",
                            label: t("commissions.bailAccount.attrs.targetAmount"),
                            children: formatLocaleCurrency(commissionsSettings.bailAccountSettings.targetAmount),
                          },
                          {
                            key: "rate",
                            label: t("commissions.bailAccount.attrs.rate"),
                            children: formatLocalePercentageNumber(commissionsSettings.bailAccountSettings.rate ?? 0),
                          },
                        ]}
                      />
                    ),
                  }
                : undefined,
              {
                key: "debtAccount",
                label: t("commissions.debtAccount.titles.page"),
                children: isTopAgentTypePlus ? (
                  <Row
                    gutter={[24, 24]}
                    style={windowSize.innerWidth < ViewBreakpoints.DESKTOP_XXL ? { maxWidth: 424 } : undefined}
                  >
                    <Col xs={24} xxl={8}>
                      <Descriptions
                        size="small"
                        bordered
                        column={1}
                        title={t("commissions.enums.commissionsCategory_PLUS.PRIMARY_TIPPER")}
                        styles={{ header: { marginBottom: 8 } }}
                        items={[
                          {
                            key: "currentBalance",
                            label: t("commissions.debtAccount.helpers.currentBalance"),
                            children: formatLocaleCurrency(commissionsSettings.primaryTipperDebtAccountBalance ?? 0),
                          },
                        ]}
                      />
                    </Col>

                    <Col xs={24} xxl={8}>
                      <Descriptions
                        size="small"
                        bordered
                        column={1}
                        title={t("commissions.enums.commissionsCategory_PLUS.PRIMARY_BROKER")}
                        styles={{ header: { marginBottom: 8 } }}
                        items={[
                          {
                            key: "currentBalance",
                            label: t("commissions.debtAccount.helpers.currentBalance"),
                            children: formatLocaleCurrency(commissionsSettings.primaryBrokerDebtAccountBalance ?? 0),
                          },
                        ]}
                      />
                    </Col>

                    <Col xs={24} xxl={8}>
                      <Descriptions
                        size="small"
                        bordered
                        column={1}
                        title={t("commissions.batch.enums.commissionBatchType_PLUS.SECONDARY")}
                        styles={{ header: { marginBottom: 8 } }}
                        items={[
                          {
                            key: "currentBalance",
                            label: t("commissions.debtAccount.helpers.currentBalance"),
                            children: formatLocaleCurrency(commissionsSettings.secondaryDebtAccountBalance ?? 0),
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                ) : (
                  <Descriptions
                    size="small"
                    column={1}
                    bordered
                    style={{ maxWidth: 400 }}
                    items={[
                      {
                        key: "currentBalance",
                        label: t("commissions.debtAccount.helpers.currentBalance"),
                        children: formatLocaleCurrency(commissionsSettings.debtAccountBalance ?? 0),
                      },
                    ]}
                  />
                ),
              },
            ].filter((item) => !!item)}
          />
        </div>

        <div style={{ flex: 1 }}>
          {isTopAgentTypePlus && (
            <Card
              title={t("commissions.settings.enums.commissionsSettingsStatus._label")}
              className="margin-bottom-medium"
              extra={
                <Space>
                  <span>{t("commissions.settings.attrs.statusEvaluationDate")}:</span>
                  <span>
                    {commissionsSettings.statusEvaluationDate ? (
                      formatLocaleDate(commissionsSettings.statusEvaluationDate)
                    ) : (
                      <span className="sub-header-info dashed">{t("common.none")}</span>
                    )}
                  </span>
                </Space>
              }
            >
              <Descriptions
                layout="vertical"
                className="margin-bottom-medium"
                style={{ textAlign: "center" }}
                styles={{ label: { textAlign: "center" } }}
                size="small"
                column={3}
                bordered
                items={[
                  {
                    label: t("commissions.settings.attrs.statusCoefficient1"),
                    children: isDefined(commissionsSettings.statusCoefficient1) ? (
                      formatLocaleNettoPoints(commissionsSettings.statusCoefficient1)
                    ) : (
                      <span className="sub-header-info dashed">{t("common.none")}</span>
                    ),
                  },
                  {
                    label: t("commissions.settings.attrs.statusCoefficient2"),
                    children: isDefined(commissionsSettings.statusCoefficient2) ? (
                      formatLocaleNettoPoints(commissionsSettings.statusCoefficient2)
                    ) : (
                      <span className="sub-header-info dashed">{t("common.none")}</span>
                    ),
                  },
                  {
                    label: t("commissions.settings.attrs.statusCoefficient3"),
                    children: isDefined(commissionsSettings.statusCoefficient3) ? (
                      formatLocaleNettoPoints(commissionsSettings.statusCoefficient3)
                    ) : (
                      <span className="sub-header-info dashed">{t("common.none")}</span>
                    ),
                  },
                ]}
              />

              {hasActionsPermission ? (
                <Select<CommissionsSettingsStatus>
                  {...selectStandardProps}
                  style={{ width: "100%" }}
                  options={Object.values(CommissionsSettingsStatus).map((status) => ({
                    value: status,
                    label: t(`commissions.settings.enums.commissionsSettingsStatus.${status}`),
                  }))}
                  value={commissionsSettings.status}
                  onChange={(value) =>
                    updateStatusCommissionsSettings.mutate({
                      id: agent.id,
                      object: {
                        optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                        status: value,
                      },
                    })
                  }
                />
              ) : (
                <Space>
                  <span>{t("commissions.settings.helpers.currentCommissionsSettingsStatus")}:</span>
                  <span>{t(`commissions.settings.enums.commissionsSettingsStatus.${commissionsSettings.status}`)}</span>
                </Space>
              )}
            </Card>
          )}

          {hasActionsPermission && (
            <Card title={t("commissions.settings.titles.settings")}>
              <Flex gap={24} vertical>
                {isTopAgentTypePlus ? (
                  <>
                    <Space size="small">
                      <Switch
                        checked={(commissionsSettings as CommissionsSettings).hasPrimaryManagerialCommissions}
                        checkedChildren={<AntIcon type="check" />}
                        unCheckedChildren={<AntIcon type="close" />}
                        onChange={() =>
                          togglePrimaryManagerialCommissions.mutate({
                            id: agent?.id,
                            object: {
                              optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                            },
                          })
                        }
                      />
                      <span>{t("commissions.settings.attrs.hasPrimaryManagerialCommissions")}</span>
                    </Space>

                    <Space size="small">
                      <Switch
                        checked={(commissionsSettings as CommissionsSettings).hasSecondaryManagerialCommissions}
                        checkedChildren={<AntIcon type="check" />}
                        unCheckedChildren={<AntIcon type="close" />}
                        onChange={() =>
                          toggleSecondaryManagerialCommissions.mutate({
                            id: agent?.id,
                            object: {
                              optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                            },
                          })
                        }
                      />
                      <span>{t("commissions.settings.attrs.hasSecondaryManagerialCommissions")}</span>
                    </Space>

                    <Space size="small">
                      <Switch
                        checked={(commissionsSettings as CommissionsSettings).allowPromotion}
                        checkedChildren={<AntIcon type="check" />}
                        unCheckedChildren={<AntIcon type="close" />}
                        onChange={() =>
                          togglePromotionAllowanceCommissionsSettings.mutate({
                            id: agent.id,
                            object: {
                              optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                            },
                          })
                        }
                      />
                      <span>{t("commissions.settings.attrs.allowPromotion")}</span>
                    </Space>
                  </>
                ) : (
                  <>
                    <Space size="small">
                      <Switch
                        checked={(commissionsSettings as CommissionsSettings).useAsHead}
                        checkedChildren={<AntIcon type="check" />}
                        unCheckedChildren={<AntIcon type="close" />}
                        onChange={() =>
                          toggleUsageAsHeadCommissionsSettings.mutate({
                            id: agent.id,
                            object: {
                              optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                            },
                          })
                        }
                      />
                      <span>{t("commissions.settings.attrs.useAsHead")}</span>
                    </Space>

                    <Space size="small">
                      <Switch
                        checked={(commissionsSettings as CommissionsSettings).unifyPointsCalculation}
                        checkedChildren={<AntIcon type="check" />}
                        unCheckedChildren={<AntIcon type="close" />}
                        onChange={() =>
                          togglePointsUnificationCommissionsSettings.mutate({
                            id: agent.id,
                            object: {
                              optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                            },
                          })
                        }
                      />
                      <span>{t("commissions.settings.attrs.unifyPointsCalculation")}</span>
                    </Space>
                  </>
                )}

                <Space size="small">
                  <Switch
                    checked={(commissionsSettings as CommissionsSettings).includeInPrimaryBulkPayment}
                    checkedChildren={<AntIcon type="check" />}
                    unCheckedChildren={<AntIcon type="close" />}
                    onChange={() =>
                      togglePrimaryPaymentInclusionCommissionsSettings.mutate({
                        id: agent.id,
                        object: {
                          optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                        },
                      })
                    }
                  />
                  <span>
                    {t(`commissions.settings.attrs.includeInPrimaryBulkPayment${isTopAgentTypePlus ? "_PLUS" : ""}`)}
                  </span>
                </Space>

                {isTopAgentTypePlus && (
                  <Space size="small">
                    <Switch
                      checked={(commissionsSettings as CommissionsSettings).includeInSecondaryBulkPayment}
                      checkedChildren={<AntIcon type="check" />}
                      unCheckedChildren={<AntIcon type="close" />}
                      onChange={() =>
                        toggleSecondaryPaymentInclusionCommissionsSettings.mutate({
                          id: agent.id,
                          object: {
                            optimisticLockVersion: (commissionsSettings as CommissionsSettings).optimisticLockVersion,
                          },
                        })
                      }
                    />
                    <span>{t("commissions.settings.attrs.includeInSecondaryBulkPayment")}</span>
                  </Space>
                )}
              </Flex>
            </Card>
          )}

          <Card
            className={classNames({ "margin-top-medium": hasActionsPermission })}
            title={t("commissions.level.sections.documents")}
          >
            <div style={{ maxHeight: 160, overflowY: "auto", paddingRight: 8 }}>
              {commissionsSettings.level?.attachments.map((attachment) => {
                return (
                  <Card
                    key={attachment.id}
                    size="small"
                    styles={{ body: { display: "flex" } }}
                    className="margin-bottom-tiny"
                  >
                    <Link
                      style={{ flex: 1, display: "flex", minWidth: 0 }}
                      to={generatePath(COMMISSIONS_LEVEL_ROUTE_PATHS.attachment.to, {
                        id1: commissionsSettings.level?.id,
                        id2: attachment.id,
                      })}
                      target="_blank"
                    >
                      <FileTypeIcon
                        contentType={attachment.file.contentType}
                        size="huge"
                        className="margin-right-tiny"
                      />
                      <Ellipsis style={{ fontSize: 14 }}>{attachment.file.filename}</Ellipsis>
                    </Link>
                  </Card>
                );
              })}

              {commissionsSettings.attachments.map((attachment) => {
                return (
                  <Card
                    key={attachment.id}
                    size="small"
                    styles={{ body: { display: "flex" } }}
                    className="margin-bottom-tiny"
                  >
                    <Link
                      style={{ flex: 1, display: "flex", minWidth: 0 }}
                      to={generatePath(AGENT_ROUTE_PATHS.commissionsSettingsAttachment.to, {
                        id1: agent.id,
                        id2: attachment.id,
                      })}
                      target="_blank"
                    >
                      <FileTypeIcon
                        contentType={attachment.file.contentType}
                        size="huge"
                        className="margin-right-tiny"
                      />
                      <Ellipsis style={{ fontSize: 14 }}>{attachment.file.filename}</Ellipsis>
                    </Link>

                    {hasActionsPermission && (
                      <Popconfirm
                        title={t("common.deleteAttachmentConfirm")}
                        icon={<PopconfirmDeleteIcon />}
                        okText={t("common.yes")}
                        cancelText={t("common.no")}
                        okType="danger"
                        onConfirm={() =>
                          deleteCommissionsSettingsAttachment.mutate({ id1: agent.id, id2: attachment.id })
                        }
                      >
                        <Button
                          className="margin-left-tiny"
                          size="small"
                          type="text"
                          danger
                          icon={<AntIcon type="delete" />}
                        />
                      </Popconfirm>
                    )}
                  </Card>
                );
              })}

              {[...(commissionsSettings.level?.attachments ?? []), ...commissionsSettings.attachments].length === 0 && (
                <Empty />
              )}
            </div>

            {hasActionsPermission && (
              <Upload multiple showUploadList={false} beforeUpload={handleAttachmentsUpload} style={{ width: "100%" }}>
                <Button
                  size="large"
                  type="dashed"
                  icon={<AntIcon type="plus" />}
                  className="margin-top-small"
                  style={{ width: "100%" }}
                >
                  {t("commissions.settings.actions.addFile")}
                </Button>
              </Upload>
            )}
          </Card>
        </div>
      </Flex>
    </>
  );
};
